/**
 * Resolving a layout against the zones a view actually contains.
 *
 * A layout names zones (`hand`), a view carries zone ids (`hand:0`, `hand:1`).
 * Bridging the two is the only structural knowledge the renderer needs, and it
 * comes from the zone declarations the server sent rather than from anything
 * hard-coded about a game.
 */

import { SEAT_END } from '@cards/toolkit';
import { describeArg, faceOf } from './cards.js';
import type { ZoneView } from '../net/match.js';
import type { ZonePlacementWire, ZoneSpecWire } from '../net/session.js';

export interface ResolvedZone {
  id: string;
  /** The seat that owns it, or null for a shared zone. */
  seat: number | null;
  placement: ZonePlacementWire;
  view: ZoneView | undefined;
}

function specFor(zones: readonly ZoneSpecWire[], name: string): ZoneSpecWire | undefined {
  for (const zone of zones) {
    if (zone.name === name) return zone;
  }
  return undefined;
}

/**
 * Every zone instance a placement refers to.
 *
 * A per-seat placement expands to one entry per seat; a shared one to exactly
 * one. Passing `onlySeat` narrows it to a single seat's instance, which is what
 * a player's own hand view wants.
 */
export function resolve(
  placement: ZonePlacementWire,
  zones: readonly ZoneSpecWire[],
  view: Record<string, ZoneView> | undefined,
  seatCount: number,
  onlySeat?: number | null,
): ResolvedZone[] {
  const spec = specFor(zones, placement.zone);
  const views = view ?? {};

  if (!spec?.perPlayer) {
    return [{ id: placement.zone, seat: null, placement, view: views[placement.zone] }];
  }

  const seats =
    onlySeat === undefined || onlySeat === null
      ? Array.from({ length: seatCount }, (_, i) => i)
      : [onlySeat];

  return seats.map((seat) => {
    const id = `${placement.zone}:${String(seat)}`;
    return { id, seat, placement, view: views[id] };
  });
}

/** The card count a seat badge should show, or null if this viewer may not know. */
export function badgeCount(
  zoneName: string,
  seat: number,
  zones: readonly ZoneSpecWire[],
  view: Record<string, ZoneView> | undefined,
): number | null {
  const spec = specFor(zones, zoneName);
  const id = spec?.perPlayer ? `${zoneName}:${String(seat)}` : zoneName;
  const zoneView = (view ?? {})[id];
  return zoneView?.count ?? null;
}

/**
 * Which cards the server says this seat may play.
 *
 * Derived from `legalMoves` and nothing else. A client that worked out
 * playability itself would be re-implementing the rules, which is the one thing
 * a thin renderer must never do — and would disagree with the server the first
 * time a rule changed.
 */
export function playableCards(
  legalMoves: ReadonlyArray<{ move: string; args: string[] }>,
  zones?: Record<string, ZoneView> | undefined,
): Set<string> {
  const cards = new Set<string>();
  for (const move of legalMoves) {
    const first = move.args[0];
    if (typeof first === 'string') cards.add(targetOf(first, zones));
  }
  return cards;
}

/**
 * The card a move's first argument points at.
 *
 * Most games name the card itself - "play the ace of spades" - and for those
 * this hands back what it was given. The patiences cannot: Golf's move names a
 * COLUMN, because which card is at the foot of it is the server's business and
 * a client asserting one could be a tick out of date. Pyramid names a SLOT,
 * because the hole a card leaves is the puzzle and two slots can hold the same
 * rank. Klondike names the pile a run starts in.
 *
 * So an argument may be a place rather than a card, in one of two shapes:
 *
 *   `col3`        a zone - the card on the end of it
 *   `pyramid:12`  a zone and an index within it
 *
 * Anything that is not a zone this viewer can see is passed through unchanged,
 * which is what keeps every game that names cards working exactly as before.
 * Resolving it here rather than in `Zone` means playability, the tap handler
 * and the marks all agree without any of them knowing which game they are in.
 */
export function targetOf(
  arg: string,
  zones: Record<string, ZoneView> | undefined,
): string {
  if (!zones) return arg;

  const colon = arg.indexOf(':');
  if (colon > 0) {
    const cards = zones[arg.slice(0, colon)]?.cards;
    if (!cards) return arg;
    const index = Number(arg.slice(colon + 1));
    if (!Number.isInteger(index) || index < 0 || index >= cards.length) return arg;
    return cards[index] as string;
  }

  const cards = zones[arg]?.cards;
  if (!cards || cards.length === 0) return arg;
  return cards[cards.length - 1] as string;
}

/**
 * Moves that name a card, grouped by that card.
 *
 * One card can carry several moves — playing an eight in Crazy Eights is a
 * different move per suit you could name — so tapping a card sometimes has to
 * ask a follow-up question rather than act.
 */
/**
 * Moves you make by tapping a PLACE on the table, grouped by that place.
 *
 * Drawing has always been a button, because the table was drawn and never
 * touched - and at a real table nobody announces "draw card", they reach for
 * the pile. The same is true of taking the discard in Rummy, and of a
 * patience, where every pile on the board is something you press.
 *
 * Read off the layout's `motions`, which already say where each move takes a
 * card FROM. That is exactly the question being asked - "what would tapping
 * here do" - so no game has to declare anything twice, and a game that
 * declares no motion for a move simply has no pile to press for it.
 *
 * The `from` end only. `playCard` travels from your seat to the discard, and
 * tapping the discard must not play a card: the card you are playing is the
 * one in your hand, and that is where the tap belongs.
 */
export function movesByZone(
  legalMoves: ReadonlyArray<{ move: string; args: string[] }>,
  motions: ReadonlyArray<{ move: string; from: string; to: string }> | undefined,
): Map<string, Array<{ move: string; args: string[] }>> {
  const byZone = new Map<string, Array<{ move: string; args: string[] }>>();
  if (!motions) return byZone;

  for (const move of legalMoves) {
    const motion = motions.find((candidate) => candidate.move === move.move);
    // SEAT_END is a seat, not a pile; there is nothing on the table to press.
    if (!motion || motion.from === SEAT_END) continue;
    const list = byZone.get(motion.from) ?? [];
    list.push(move);
    byZone.set(motion.from, list);
  }
  return byZone;
}

/**
 * What one extra argument of a move OFFERS, in words.
 *
 * The chooser exists for a move that comes in several flavours, and the extra
 * arguments are the flavours: the suit an eight names, the pile a Klondike
 * card could go to, the partner a Pyramid card could pair with. It was showing
 * them raw, which reads as "hearts" for the first and "19foundationC" for the
 * second.
 *
 * Three kinds, and the difference is readable without knowing any game:
 *
 *   - An index is an ADDRESS, not a choice. `moveCards` carries the position
 *     in the pile a run starts at; nobody is choosing 19. Dropped.
 *   - A place holding a card is the CARD there - "pair with the 7 of
 *     diamonds" is the decision, not "waste:5".
 *   - A place holding none is the place itself, by whatever the layout calls
 *     it. An empty foundation is a real destination and has to say so.
 *
 * `labels` comes from the layout, so a zone nobody named falls back to its own
 * id rather than to nothing.
 */
export function choiceLabel(
  arg: string,
  zones: Record<string, ZoneView> | undefined,
  labels: ReadonlyMap<string, string>,
): { text: string; red: boolean } | null {
  if (/^\d+$/.test(arg)) return null;

  const card = targetOf(arg, zones);
  if (card !== arg) {
    // A card as it is written on its face, not as it is stored: "2 spade"
    // rather than "2S". `describeArg` knows suits, not whole cards.
    const face = faceOf(card);
    return { text: `${face.rank} ${face.suit}`.trim(), red: face.red };
  }

  const zone = arg.indexOf(':') > 0 ? arg.slice(0, arg.indexOf(':')) : arg;
  const label = labels.get(zone);
  if (label !== undefined) return { text: label, red: false };
  if (zones?.[zone] !== undefined) return { text: zone, red: false };

  const described = describeArg(arg);
  return described ? { text: described.text, red: described.red } : { text: arg, red: false };
}

export function movesByCard(
  legalMoves: ReadonlyArray<{ move: string; args: string[] }>,
  zones?: Record<string, ZoneView> | undefined,
): Map<string, Array<{ move: string; args: string[] }>> {
  const byCard = new Map<string, Array<{ move: string; args: string[] }>>();
  for (const move of legalMoves) {
    const first = move.args[0];
    if (typeof first !== 'string') continue;
    // Keyed by the card the argument POINTS AT, so a move that names a place
    // hangs off the card sitting there. See `targetOf`.
    const card = targetOf(first, zones);
    const list = byCard.get(card) ?? [];
    list.push(move);
    byCard.set(card, list);
  }
  return byCard;
}

/**
 * How many columns a board is wide, from what the game actually declares.
 *
 * The grid was `repeat(8, ...)`, which is Klondike's board and nobody else's.
 * Pyramid is seven across and Golf is seven across, so both were laid out in
 * eight columns and used seven of them - and a board that fills 7/8 of a
 * centred grid sits half a column to the left of the middle. That is P1: 73px
 * at 1400, 183px at 2560, growing with the window, and it was never an
 * alignment to nudge. The board was the wrong width.
 *
 * Counted as the furthest cell any placement reaches, so a zone spanning seven
 * columns from the first counts as seven.
 */
export function boardColumns(placements: readonly ZonePlacementWire[]): number {
  let widest = 0;
  for (const placement of placements) {
    if (placement.col === undefined) continue;
    widest = Math.max(widest, placement.col + (placement.span ?? 1) - 1);
  }

  /*
   * Eight for a board that declares no columns at all, which is what the grid
   * did before this existed. Such a layout has nothing to be centred against -
   * its zones auto-flow - so this keeps it exactly as it was rather than
   * inventing a width for it.
   */
  return widest > 0 ? widest : 8;
}
